import {
  Autocomplete,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { ComboBoxType } from "../../../../../types/general";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { findCenterCombo, findInterfaceCombo } from "../../../../../services/logInterface";
import dayjs from "dayjs";
import DatePickerWrapper from "../../../../../components/wrappers/DatePickerWrapper";

interface SearchLogProps {
  setParams: (params: any) => void;
  handleSearch: (params?: any) => void;
  setHasBeenSearched: (params?: any) => void;
}

const integrationTypes: ComboBoxType[] = [
    { id: 'null', description: 'All' },
    { id: 'successfully', description: 'Successes' },
    { id: 'warnings', description: 'Warnings' },
    { id: 'errors', description: 'Errors' }
]

interface IPeriods {
    month: Date
    twoweeks: Date
    oneweek: Date
    today: Date
    custom: null
}

const periodsObjects: IPeriods = {
    month: dayjs(new Date()).subtract(1, 'month').toDate(),
    twoweeks: dayjs(new Date()).subtract(2, 'weeks').toDate(),
    oneweek: dayjs(new Date()).subtract(7, 'days').toDate(),
    today: new Date(),
    custom: null
}

const periods: ComboBoxType[] = [
    { id: 'custom', description: 'Custom' },
    { id: 'month', description: 'One Month' },
    { id: 'twoweeks', description: 'Two Weeks' },
    { id: 'oneweek', description: 'One Week' },
    { id: 'today', description: 'Today' }
]

const ediFileScheme = yup.object({
  searchGeneral: yup.string().notRequired(),
  ID_CENTRO: yup.string().notRequired(),
  ID_INTEGRACAO_SAP: yup.string().required(),
  RESPONSE_TYPE: yup.string().notRequired(),
  PERIOD: yup.string().required(),
  DATA_OCORRENCIA_INICIO: yup.string().notRequired(),
  DATA_OCORRENCIA_FIM: yup.string().notRequired(),
});

export default function SearchLog({
  setParams,
  setHasBeenSearched,
  handleSearch,
}: SearchLogProps) {

  const [centerCombo, setCenterCombo] = useState<ComboBoxType[]>([]);
  const [interfaceCombo, setInterfaceCombo] = useState<ComboBoxType[]>([]);

  const {
    handleSubmit,
    control,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(ediFileScheme),
    defaultValues: {
        PERIOD: 'month',
        RESPONSE_TYPE: 'null',
        DATA_OCORRENCIA_INICIO: dayjs(periodsObjects['month']).toISOString().split('T')[0]
    }
  });

  const handleFormSearch = (searchParams: any) => {
    setHasBeenSearched(true)
    setParams((prevState: any) => ({
      ...prevState,
      page: 1,
      searchGeneral: searchParams.searchGeneral,
      ID_CENTRO: searchParams.ID_CENTRO,
      ID_INTEGRACAO_SAP: searchParams.ID_INTEGRACAO_SAP,
      RESPONSE_TYPE: searchParams.RESPONSE_TYPE,
      PERIOD: searchParams.PERIOD,
      DATA_OCORRENCIA_INICIO: searchParams.DATA_OCORRENCIA_INICIO ? searchParams.DATA_OCORRENCIA_INICIO.split('T')[0] : undefined,
      DATA_OCORRENCIA_FIM: searchParams.DATA_OCORRENCIA_FIM ? searchParams.DATA_OCORRENCIA_FIM.split('T')[0] + ' 23:59:59.999' : undefined,
    }));
  }

    useEffect(() => {
        const fetchData = async () => {
            const [findCenter, findInterface] = await Promise.all([findCenterCombo(), findInterfaceCombo()]);

            setCenterCombo(findCenter);
            setInterfaceCombo(findInterface);
        };

        fetchData();
    }, []);

    const customPeriodVisible = watch('PERIOD');

    return (
    <form
      onSubmit={handleSubmit(handleFormSearch)}
      style={{ width: "100%" }}
      data-testid="edi-file-form"
    >
        <Grid container spacing={2} marginBottom={2} marginTop={4} rowGap={1}>
            <Grid item xs={6} md={3}>
                <Controller
                    control={control}
                    name="searchGeneral"
                    render={({field: { value, onChange, ...field}}) => (
                        <TextField
                            data-testid="search-searchGeneral-input"
                            fullWidth
                            type="text"
                            label="General Search"
                            size="small"
                            onChange={(e) => onChange(e.target.value.trim())}
                            onKeyDown={(event: React.KeyboardEvent) => {
                                if (event.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid item xs={6} md={3}>
                <Controller
                    control={control}
                    name="ID_CENTRO"
                    render={({ field: { value, onChange, ...field } }) => (
                        <Autocomplete
                            options={centerCombo || []}
                            getOptionLabel={(option: ComboBoxType) => `${option.description}`}
                            value={centerCombo?.find((item: any) => item.id === value) || null}
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    {...field}
                                    label="Company/Plant"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            onChange={(_, data: any) => onChange(data?.id)}
                        />
                    )}
                />
            </Grid>

            <Grid item xs={6} md={3}>
                <Controller
                    control={control}
                    name="ID_INTEGRACAO_SAP"
                    render={({ field: { value, onChange, ...field } }) => (
                        <Autocomplete
                            options={interfaceCombo || []}
                            getOptionLabel={(option: ComboBoxType) => `${option.description}`}
                            value={interfaceCombo?.find((item: any) => item.id === value) || null}
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    {...field}
                                    label="Interface Name"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    required
                                />
                            )}
                            onChange={(_, data: any) => onChange(data?.id)}
                            
                        />
                    )}
                />
            </Grid>

            <Grid item xs={6} md={3}>
                <Controller
                    control={control}
                    name="RESPONSE_TYPE"
                    render={({ field: { value, onChange, ...field } }) => (
                        <Autocomplete
                            options={integrationTypes || []}
                            getOptionLabel={(option: ComboBoxType) => `${option.description}`}
                            value={integrationTypes?.find((item: any) => item.id === value) || null}
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    {...field}
                                    label="Response Type"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            onChange={(_, data: any) => onChange(data?.id)}
                            
                        />
                    )}
                />
            </Grid>

            <Grid item xs={6} md={3}>
                <Controller
                    control={control}
                    name="PERIOD"
                    render={({ field: { value, onChange, ...field } }) => (
                        <Autocomplete
                            options={periods || []}
                            getOptionLabel={(option: ComboBoxType) => `${option.description}`}
                            value={periods?.find((item: any) => item.id === value) || null}
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    {...field}
                                    label="Period"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    required
                                />
                            )}
                            onChange={(_, data: ComboBoxType | null) => {
                                if(!data) {
                                    onChange(null);
                                    return;
                                }
                                onChange(data.id);

                                type PeriodKey = keyof typeof periodsObjects;

                                if(typeof data.id === 'string' && data.id in periodsObjects) {
                                    const dateFromPeriod = periodsObjects[data.id as PeriodKey];
                                    if(!dateFromPeriod) return;
                                    setValue("DATA_OCORRENCIA_INICIO", dayjs(dateFromPeriod).toISOString());
                                    setValue("DATA_OCORRENCIA_FIM", null);
                                }
                            }}
                        />
                    )}
                />
            </Grid>

            {/* Ajuste para os DatePickers ficarem lado a lado */}
            {customPeriodVisible === 'custom' && (
                <>
                <Grid item xs={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                            name="DATA_OCORRENCIA_INICIO"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <DatePickerWrapper
                                    label="Initial Date"
                                    value={value ? dayjs(value) : null}
                                    onChange={(date) => onChange(dayjs(date)?.isValid() ? dayjs(date).toISOString() : null)}
                                    slotProps={{
                                        textField: {
                                            variant: "outlined",
                                            fullWidth: true,
                                            size: "small",
                                        },
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                            name="DATA_OCORRENCIA_FIM"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <DatePickerWrapper
                                    label="Final Date"
                                    value={value ? dayjs(value) : null}
                                    onChange={(date) => onChange(dayjs(date)?.isValid() ? dayjs(date).toISOString() : null)}
                                    slotProps={{
                                        textField: {
                                            variant: "outlined",
                                            fullWidth: true,
                                            size: "small",
                                        },
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Grid>
                </>
            )}

            <Grid item xs={12} md={2}>
                <Button
                    startIcon={<SearchIcon />}
                    variant="outlined"
                    data-testid="submit-button"
                    type="submit"
                    fullWidth
                >
                    Search
                </Button>
            </Grid>
        </Grid>

    </form>
  );
}
